/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.14.0
 */

import { Fragment, useCallback } from 'react';

import { connect, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EBasketType } from '@modules/app/appConstants';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalAccounts,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IWizardAdditionalAccountsTranslation } from '@modules/internationalization/types/wizards/additionalAccounts';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';

import Title from '../../../components/common/Title/Title';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import Wizard from '../../../modules/wizard/components/Wizard';
import { EAdditionalAccountType, IWizardAdditionalAccountsStartData } from '../types/wizardAdditionalAccountsTypes';
import { wizardAdditionalAccountsHandleNextAction } from '../wizardAdditionalAccountsActions';
import { EWizardAdditionalAccountsSteps } from '../wizardAdditionalAccountsConstant';
import wizardAdditionalAccountsReducer, { IWizardAdditionalAccountsReducer } from '../wizardAdditionalAccountsReducer';
import { makeSelectWizardAdditionalAccountsValidation } from '../wizardAdditionalAccountsSelectors';
import WizardAdditionalAccountsConfirmation from './WizardAdditionalAccountsConfirmation';
import WizardAdditionalAccountsSelectParams from './WizardAdditionalAccountsSelectParams';

const WizardAdditionalAccounts = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translationAdditionalAccounts,
        validation,

        handleNext,
    } = props;

    const { subscriberId } = useParams<IRouteParams>();

    const location = useLocation();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { persAccMaster } } = translationCommon;

    /**
     * Load data to start wizard from SubscriberId page
     */
    const getStartData = useCallback((): IWizardAdditionalAccountsStartData => {
        const locationHash = location.hash.replace('#', '');
        return subscriberId && locationHash
            ? { accountType: locationHash as EAdditionalAccountType }
            : {};
    }, [location, subscriberId]);

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardAdditionalAccountsSteps.StepSelectParams:
                return <WizardAdditionalAccountsSelectParams/>;
            case EWizardAdditionalAccountsSteps.StepConfirmation:
                return <WizardAdditionalAccountsConfirmation/>;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardAdditionalAccounts,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardAdditionalAccounts}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                entityId={subscriberId ? parseInt(subscriberId) : WIZARD_ALL_ENTITIES}
                getStartData={getStartData}
                instructionLink={persAccMaster}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.ADDITIONAL_ACCOUNTS}
                translationProps={{
                    error: translationAdditionalAccounts?.error,
                    steps: translationAdditionalAccounts?.steps,
                    title: translationAdditionalAccounts?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>,
    Pick<IWizardAdditionalAccountsReducer, 'validation'> {
    translationAdditionalAccounts: IWizardAdditionalAccountsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translationAdditionalAccounts: makeSelectTranslationWizardAdditionalAccounts(),
    validation: makeSelectWizardAdditionalAccountsValidation(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardAdditionalAccountsHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalAccounts),
    { reducer: { wizardAdditionalAccounts: wizardAdditionalAccountsReducer } },
);
